import type { Environment } from '@hangar/shared';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EnvVarsTable } from './EnvVarsTable';
import { RequirementsEditor } from './RequirementsEditor';
import { ResolvePreview } from './ResolvePreview';
import { ScopePicker } from './ScopePicker';
import type { EnvDialogsApi } from './useEnvDialogs';
import type { EnvPageApi } from './useEnvPage';

interface Props {
  page: EnvPageApi;
  dialogs: EnvDialogsApi;
  environments: Environment[];
  tab: string;
}

/** The three sections of the page: variables at a scope, the Resolve preview and the requirements editor. */
export function EnvVarsTabs({ page, dialogs, environments, tab }: Props) {
  const { scope } = page;
  const apps = page.apps.data;
  return (
    <Card className="enter overflow-hidden">
      <Tabs value={tab} onValueChange={page.setTab}>
        <TabsList variant="line" className="px-2" aria-label="Environment sections">
          <TabsTrigger value="variables" data-testid="env-tab-variables">
            Variables
          </TabsTrigger>
          <TabsTrigger value="resolve" data-testid="env-tab-resolve">
            Resolve
          </TabsTrigger>
          <TabsTrigger value="requirements" data-testid="env-tab-requirements">
            Requirements
          </TabsTrigger>
        </TabsList>
        <TabsContent value="variables">
          <VariablesTab page={page} dialogs={dialogs} environments={environments} />
        </TabsContent>
        <TabsContent value="resolve">
          <ResolvePreview apps={apps} environments={environments} initialAppSlug={scope.appSlug} initialEnvironment={scope.environment} initialPlatform={scope.platform} />
        </TabsContent>
        <TabsContent value="requirements">
          <RequirementsEditor apps={apps} initialAppSlug={scope.appSlug} />
        </TabsContent>
      </Tabs>
    </Card>
  );
}

function VariablesTab({ page, dialogs, environments }: Omit<Props, 'tab'>) {
  const { vars } = page;
  return (
    <>
      <div className="border-b p-4 sm:p-5">
        <ScopePicker value={{ ...page.scope, includeInherited: page.inherited }} onChange={page.setScope} apps={page.apps.data} environments={environments} />
      </div>
      <CardContent className="border-t-0 p-0 sm:p-0">
        <EnvVarsTable
          vars={page.rows}
          isLoading={vars.isLoading}
          isError={vars.isError}
          error={vars.error}
          onRetry={() => void vars.refetch()}
          onCreate={() => dialogs.setCreating(true)}
          scopeReady={page.scopeReady}
          onEdit={dialogs.setEditing}
          onNewVersion={dialogs.setVersionFor}
          onReveal={dialogs.setRevealFor}
          onVersions={dialogs.setVersionsFor}
          onDelete={dialogs.setDeleting}
        />
      </CardContent>
    </>
  );
}
